import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { orders } from '../api/endpoints.ts';
import type { OrderListItem, SupplementQuote } from '../api/types.ts';
import { formatPara, para } from '../core/money.ts';
import { errorText as textOf, orderMoment, useLang, useStrings } from '../i18n/index.ts';
import { cartStore, clearCart, orderItems } from '../state/cart.ts';
import { openOrder } from '../state/router.ts';
import { notify } from '../state/toasts.ts';
import { pokeWatcher } from '../state/watcher.ts';
import { Button, Card, Spinner } from '../ui/kit.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import '../styles/cart.css';

/**
 * Корзина добавляется в заказ, который ещё собирают: сервер заново оценивает состав (акции,
 * бесплатная доставка) и списывает только разницу. Лист показывает доплату и новый итог,
 * после оплаты корзина пустеет и открывается заказ. Заказ, который за это время собрали,
 * отвечает отказом: корзина остаётся для нового заказа.
 */
export function SupplementSheet({ order, onClose }: { order: OrderListItem; onClose: () => void }) {
  const strings = useStrings();
  const lang = useLang();
  const [quote, setQuote] = useState<SupplementQuote | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);
  const [isPaying, setPaying] = useState(false);

  const show = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'order_already_picked') {
      setClosed(true);
      setErrorText(strings.supplementPicked);
      return;
    }
    setErrorText(textOf(strings, error));
  };

  useEffect(() => {
    let cancelled = false;
    orders
      .supplementQuote(order.id, orderItems(cartStore.get()))
      .then((next) => {
        if (!cancelled) setQuote(next);
      })
      .catch((error: unknown) => {
        if (!cancelled) show(error);
      });
    return () => {
      cancelled = true;
    };
    // Доплата считается один раз на открытие листа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const pay = async () => {
    setPaying(true);
    try {
      const result = await orders.supplement(order.id, orderItems(cartStore.get()));
      if (result.status !== 'paid') {
        setErrorText(strings.supplementDeclined);
        return;
      }
      clearCart();
      pokeWatcher();
      notify(strings.supplementDone);
      openOrder(order.id);
    } catch (error) {
      show(error);
    } finally {
      setPaying(false);
    }
  };

  const charge = para(quote?.chargeRsd);
  const title = strings.supplementTitle(orderMoment(lang, order.placedAt));

  return (
    <Sheet size="medium" locked={isPaying} label={title} onClose={onClose}>
      {(close) => (
        <>
          <div className="sheet-header supplement-header">
            <button type="button" className="text-button" disabled={isPaying} onClick={close}>
              {strings.cancel}
            </button>
            <h2>{title}</h2>
            <span />
          </div>
          <div className="sheet-body sheet-pad stack supplement-body">
            {quote ? (
              <Card>
                <div className="stack" style={{ gap: 10 }}>
                  <div className="row">
                    <span className="t-subheadline t-muted grow">{strings.supplementCharge}</span>
                    <b className="t-numeric supplement-charge">{formatPara(charge)}</b>
                  </div>
                  <div className="row">
                    <span className="t-subheadline t-muted grow">{strings.supplementNewTotal}</span>
                    <span className="t-numeric">{formatPara(para(quote.totalAfterRsd))}</span>
                  </div>
                </div>
              </Card>
            ) : (
              errorText === null && (
                <div className="supplement-loading">
                  <Spinner />
                </div>
              )
            )}
            {errorText && (
              <p className="t-subheadline supplement-error" role="alert">
                {errorText}
              </p>
            )}
            <span className="grow" />
            {quote && !closed && (
              <Button busy={isPaying} onClick={() => void pay()}>
                {charge > 0n ? strings.supplementPay(formatPara(charge)) : strings.supplementAdd}
              </Button>
            )}
          </div>
        </>
      )}
    </Sheet>
  );
}
